<template>
	<v-row dense>
		<v-col cols="12" lg="7">
			<SectionCard title="Conto economico" icon="mdi-scale-balance" subtitle="importi al netto dell'IVA" data-tour="pnl">
				<FinanceStatement :rows="rows" :compare="!!previous" :current-label="currentLabel" :previous-label="previousLabel" />
				<div class="fs-note mt-3">
					Il costo del venduto usa il prezzo d'acquisto attuale del catalogo (acquisto × pezzi per unità) e copre il {{ formatPct(pnl.cogsCoverage) }} delle vendite nette: i prodotti senza costo sono
					esclusi dal costo, non contati a zero. La merce comprata nel periodo non è una spesa qui, perché la copre il costo del venduto.
				</div>
			</SectionCard>
		</v-col>
		<v-col cols="12" lg="5" class="fp-stack">
			<SectionCard title="Commissioni per fonte" icon="mdi-percent-outline" no-padding data-tour="fees">
				<template #actions>
					<v-btn text x-small color="primary" @click="$emit('open-fees')">Gestisci</v-btn>
				</template>
				<v-simple-table dense>
					<tbody>
						<tr v-for="f in fees" :key="f.id">
							<td>
								{{ sourceName(f.key) }}
								<span class="fs-note">· {{ f.scope === 'channel' ? 'canale' : 'pagamento' }}</span>
							</td>
							<td>
								<v-chip x-small label outlined :color="FEE_ORIGIN[f.origin].color">{{ FEE_ORIGIN[f.origin].label }}</v-chip>
							</td>
							<td class="text-right">{{ f.origin === 'none' ? '—' : formatEurCents(f.amount) }}</td>
						</tr>
						<tr v-if="!fees.length">
							<td colspan="3" class="text-center grey--text py-4">Nessuna commissione nel periodo.</td>
						</tr>
					</tbody>
				</v-simple-table>
				<div v-if="missing" class="fs-note pa-3">
					{{ missing }} {{ missing === 1 ? 'fonte senza' : 'fonti senza' }} commissione: se non hanno un'API, inserisci il totale del mese o la tariffa in Commissioni.
				</div>
			</SectionCard>
			<SectionCard title="Spese per categoria" icon="mdi-format-list-bulleted" no-padding data-tour="expenses" class="mt-2">
				<v-simple-table dense>
					<tbody>
						<tr v-for="c in categories" :key="c.name">
							<td>{{ c.name }}</td>
							<td class="text-right">{{ formatEurCents(c.value) }}</td>
						</tr>
						<tr v-if="!categories.length">
							<td colspan="2" class="text-center grey--text py-4">Nessuna spesa nel periodo. Le spese si inseriscono da <a @click="$emit('open-costs')">Costi</a>.</td>
						</tr>
					</tbody>
				</v-simple-table>
			</SectionCard>
			<SectionCard title="Acquisti merce del periodo" icon="mdi-truck-delivery-outline" subtitle="informativo" class="mt-2">
				<div class="d-flex align-center">
					<div>
						<div class="text-h6">{{ formatEurCents(merchandise.net) }}</div>
						<div class="fs-note">{{ merchandise.docs || 0 }} fatture XML · IVA {{ formatEurCents(merchandise.vat) }}</div>
					</div>
				</div>
			</SectionCard>
		</v-col>
	</v-row>
</template>

<script>
import SectionCard from '@/components/core/SectionCard.vue';
import FinanceStatement from './FinanceStatement.vue';
import { formatEurCents, formatPct, sourceName, FEE_ORIGIN } from './format';

// provenienza delle commissioni di un gruppo (marketplace o pagamento)
const originBadge = part => {
	if (!part || !part.total) return null;
	const kinds = ['api', 'manual', 'estimated'].filter(k => part[k]);
	if (kinds.length > 1) return { text: 'fonti miste', color: 'grey darken-1' };
	if (kinds[0] === 'estimated') return { text: 'da tariffa', color: 'orange darken-2' };
	if (kinds[0] === 'manual') return { text: 'manuali', color: 'primary' };
	return { text: 'da API', color: 'green darken-1' };
};

export default {
	name: 'FinancePnl',
	components: { SectionCard, FinanceStatement },
	props: {
		report: { type: Object, required: true },
		previous: { type: Object, default: null },
		currentLabel: { type: String, default: '' },
		previousLabel: { type: String, default: '' },
		platform: { type: String, default: '' },
	},
	computed: {
		pnl() {
			return this.report.pnl || {};
		},
		fees() {
			return (this.pnl.fees || []).filter(f => f.origin !== 'none' || f.orders);
		},
		missing() {
			return this.fees.filter(f => f.origin === 'none').length;
		},
		merchandise() {
			return this.pnl.merchandisePurchases || {};
		},
		categories() {
			return Object.entries((this.pnl.expenses && this.pnl.expenses.byCategory) || {})
				.map(([name, value]) => ({ name, value }))
				.sort((a, b) => b.value - a.value);
		},
		rows() {
			const c = this.pnl;
			const p = (this.previous && this.previous.pnl) || {};
			const get = (obj, path) => path.split('.').reduce((o, k) => (o ? o[k] : undefined), obj);
			const row = (label, path, extra = {}) => ({ label, value: get(c, path), previous: get(p, path), ...extra });
			const rows = [
				row('Vendite nette dei prodotti', 'netSales', { indent: true }),
				row('Spedizioni pagate dai clienti', 'shipping', { indent: true }),
				row('Supplementi pagati dai clienti', 'surcharges', { indent: true }),
				row('Ricavi', 'revenue', { total: true }),
				row('Costo del venduto', 'cogs', { negative: true, hint: 'Al netto della merce rientrata a magazzino con i resi.' }),
				row('Margine lordo', 'grossProfit', { total: true }),
				row('Commissioni marketplace', 'marketplaceFees.total', { negative: true, indent: true, badge: originBadge(c.marketplaceFees) }),
				row('Commissioni pagamento', 'paymentFees.total', { negative: true, indent: true, badge: originBadge(c.paymentFees) }),
				row('Provvigioni agenti e partner', 'commissions.total', { negative: true, indent: true }),
			];
			// pubblicita' letta dalle piattaforme (Report -> Pubblicita'): non si ripartisce sui canali
			if (!this.platform && ((c.ads && c.ads.total) || (p.ads && p.ads.total))) {
				rows.push(
					row('Pubblicità', 'ads.total', {
						negative: true,
						indent: true,
						hint: 'Spesa letta da Google Ads e dalle altre piattaforme collegate. I costi manuali «Pubblicità» della stessa piattaforma e le sue fatture non si sommano.',
					}),
				);
			}
			rows.push(row('Margine di contribuzione', 'contribution', { total: true }));
			// le spese non sono attribuibili a un canale: con il filtro canale ci si ferma al margine di contribuzione
			if (!this.platform) {
				rows.push(
					row('Spese da fatture passive', 'expenses.passive', { negative: true, indent: true }),
					row('Costi manuali', 'expenses.manual', { negative: true, indent: true, hint: 'Personale, affitto e altre spese senza XML, con la loro IVA indetraibile.' }),
					row('Risultato operativo', 'operatingResult', { total: true }),
				);
			}
			return rows;
		},
	},
	data() {
		return { FEE_ORIGIN };
	},
	methods: { formatEurCents, formatPct, sourceName },
};
</script>

<style scoped>
/* card una sotto l'altra: l'altezza al 100% di SectionCard le farebbe uscire dalla colonna */
.fp-stack ::v-deep .section-card {
	height: auto;
}
.fs-note {
	font-size: 12px;
	color: #5f6b7a;
}
</style>
